import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import SimulationWorkspace from '@/components/Layout/SimulationWorkspace';
import ScenarioBriefing from '@/components/Layout/ScenarioBriefing';
import { api } from '@/services/api';
import { NotificationService } from '@/services/notifications';
import { useSessionStore } from '@/stores';
import { ScenarioDefinition } from '@/types';

const SimulationView: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const {
    session,
    materializedView,
    setSession,
    setMaterializedView,
    setLoading,
    error,
    setError
  } = useSessionStore();

  const [initialLoad, setInitialLoad] = useState(true);
  const [scenario, setScenario] = useState<ScenarioDefinition | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const notificationServiceRef = useRef<NotificationService | null>(null);

  // Polling interval ref for cleanup
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchState = useCallback(async () => {
    if (!sessionId) return;
    try {
      const stateResponse = await api.getSessionState(sessionId);
      setSession(stateResponse.session);
      setMaterializedView(stateResponse.materializedView);
    } catch (err) {
      console.error('Error fetching session state:', err);
    }
  }, [sessionId, setSession, setMaterializedView]);

  const startPollingAndWebSocket = useCallback(() => {
    if (!sessionId) return;

    // Set up WebSocket for real-time updates
    const ns = new NotificationService();
    notificationServiceRef.current = ns;
    ns.connect(sessionId, fetchState);

    // Fallback polling every 3 seconds
    pollingRef.current = setInterval(fetchState, 3000);
  }, [sessionId, fetchState]);

  const cleanupPolling = useCallback(() => {
    if (notificationServiceRef.current) {
      notificationServiceRef.current.disconnect();
      notificationServiceRef.current = null;
    }
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    loadSessionState();

    return () => {
      cleanupPolling();
    };
  }, [sessionId, navigate]);

  // Start polling only when session is active (not during briefing)
  useEffect(() => {
    if (session && session.status !== 'NotStarted') {
      startPollingAndWebSocket();
    }
    return () => {
      cleanupPolling();
    };
  }, [session?.status]);

  const loadSessionState = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setError(null);

      const stateResponse = await api.getSessionState(sessionId);
      setSession(stateResponse.session);
      setMaterializedView(stateResponse.materializedView);

      // If session is NotStarted, load the scenario for briefing content
      if (stateResponse.session.status === 'NotStarted') {
        try {
          const scenarioData = await api.getScenario(stateResponse.session.scenarioId);
          setScenario(scenarioData);
        } catch (err) {
          console.error('Error loading scenario:', err);
        }
      }
    } catch (error) {
      console.error('Error loading session state:', error);
      setError('Failed to load simulation session');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleStart = async () => {
    if (!sessionId) return;

    setIsStarting(true);
    try {
      const updatedSession = await api.startSession(sessionId);
      setSession(updatedSession);
      // Re-fetch full state to get fresh MV with reset times
      await fetchState();
    } catch (err) {
      console.error('Error starting session:', err);
      setError('Failed to start simulation');
    } finally {
      setIsStarting(false);
    }
  };

  if (initialLoad) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!session || !materializedView) {
    return (
      <Box p={4}>
        <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
          Session not found or expired
        </Alert>
      </Box>
    );
  }

  // Show briefing screen when session hasn't started yet
  if (session.status === 'NotStarted') {
    const briefing = scenario?.briefing || {
      role: 'Operations Manager',
      context: 'You have a busy morning ahead. Your inbox needs attention and your team is waiting.',
      objectives: ['Manage your communications', 'Make decisions under pressure'],
      tips: ['Read emails carefully', 'Respond to urgent items first'],
    };

    return (
      <ScenarioBriefing
        title={scenario?.title || 'Simulation'}
        duration={scenario?.duration || 900}
        difficulty={scenario?.difficulty || 'Intermediate'}
        briefing={briefing}
        onStart={handleStart}
        isStarting={isStarting}
      />
    );
  }

  return (
    <SimulationWorkspace
      session={session}
      materializedView={materializedView}
    />
  );
};

export default SimulationView;
